/**
 * Collapses a text block to a line count and reveals the rest on request.
 *
 * The clamp itself is CSS; this element only decides whether the control is
 * needed and flips the state. That ordering matters: the copy is readable with
 * the script absent or still loading, and the button only appears once we know
 * there is something hidden behind it.
 */
class TextClamp extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#toggle = this.querySelector('.text-clamp__toggle');
    if (!this.#toggle) return;

    this.#toggle.addEventListener('click', this.#handleClick);

    /* Re-measure on resize: a narrower column wraps to more lines, so copy that
       fitted on desktop may need the control on a phone, and the other way
       about after an orientation change. */
    this.#observer = new ResizeObserver(() => this.#syncToggle());
    this.#observer.observe(this);

    this.#syncToggle();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#handleClick);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @returns {HTMLElement | null} The element carrying the clamp. */
  get #text() {
    return this.querySelector(':scope > div, :scope > rte-formatter, :scope > p');
  }

  /**
   * Show the control only when the copy is actually taller than its clamp.
   * Measured while collapsed, since an expanded element always overflows.
   */
  #syncToggle = () => {
    if (!this.#toggle) return;
    if (this.dataset.expanded === 'true') return;

    const text = this.#text;
    if (!text) return;

    const overflows = text.scrollHeight - text.clientHeight > 1;
    this.#toggle.hidden = !overflows;
  };

  #handleClick = () => {
    if (!this.#toggle) return;

    const expanded = this.dataset.expanded === 'true';

    if (expanded) {
      delete this.dataset.expanded;
    } else {
      this.dataset.expanded = 'true';
    }

    this.#toggle.setAttribute('aria-expanded', String(!expanded));
    this.#toggle.textContent = expanded
      ? this.dataset.more || 'Show more'
      : this.dataset.less || 'Show less';

    /* Collapsing can leave the reader below the fold of the shortened block. */
    if (expanded) this.#syncToggle();
  };
}

if (!customElements.get('text-clamp')) {
  customElements.define('text-clamp', TextClamp);
}
